import { Text } from "@solomon-labs/design/components/ui";
import type React from "react";
import { useState } from "react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";

import { GRAPH_BG_CLASS, type GraphSlot } from "../../utils/graphColors";

import { formatLongDate, formatShortDate } from "./chartFormat";
import {
    INLINE_CHART_STYLE,
    InlineInspectionStyle,
    InlineReadout,
    InlineReadoutSpacer,
    squareActiveDot,
    useChartInspection
} from "./chartInspection";

/**
 * A row the multi-area chart consumes — one timestamp plus one numeric
 * value per visible series, keyed by the series `key`.
 */
export type MultiAreaRow = {
    /** X value — epoch milliseconds. */
    t: number;
} & Record<string, number>;

/** One area series. */
export interface MultiAreaSeries {
    /** Data key into each row. */
    key: string;
    /** Legend + tooltip display name. */
    name: string;
    /** Line + fill color as a CSS color string — pass `graphColor(slot)`. */
    color: string;
    /** Slot backing the swatch `bg-graph-*` class. */
    slot: GraphSlot;
}

export interface MultiAreaChartProps {
    /** Already-windowed rows, ascending by `t`. */
    data: MultiAreaRow[];
    /** Series, in draw order. */
    series: MultiAreaSeries[];
    /**
     * Unique prefix for the `<linearGradient>` defs (combined with each
     * series key). Must be page-unique.
     */
    gradientIdPrefix: string;
    /** Formats the tooltip value (full precision). */
    valueFormatter: (value: number) => string;
    /** Formats the Y-axis ticks (compact). */
    yTickFormatter: (value: number) => string;
    /** Formats the X-axis ticks. Defaults to `formatShortDate`. */
    dateFormatter?: (value: number) => string;
    /** Formats the tooltip date label. Defaults to `formatLongDate`. */
    tooltipDateFormatter?: (value: number) => string;
    /** Chart height in px. Default 240. */
    height?: number;
    /**
     * Instruction text shown at the legend's right edge. Defaults to
     * the Balances report copy. Pass `null` to omit.
     */
    legendInstruction?: React.ReactNode;
    /**
     * Render the visibility-toggle legend. Off when the series are already
     * named beside the chart and a second list would only repeat them.
     */
    showLegend?: boolean;
    /** Inline value/date inspection, used by the main Stats page. */
    inspection?: "tooltip" | "inline";
}

/**
 * `MultiAreaChart` — multi-series gradient area chart with an
 * interactive visibility-toggle legend.
 *
 * Extracted from the YaaS Client Console Balances report: one
 * `<linearGradient>` (0.85 → 0.25) + `<Area>` per series, 0.08
 * horizontal gridlines, ABC Social axes, dark inverse tooltip listing
 * every visible series, and a footer legend whose rows toggle series
 * visibility (line-through + dimmed when hidden). The hidden-state is
 * owned internally because it is intrinsic to this chart's interaction.
 */
export function MultiAreaChart({
    data,
    series,
    gradientIdPrefix,
    valueFormatter,
    yTickFormatter,
    dateFormatter = formatShortDate,
    tooltipDateFormatter = formatLongDate,
    height = 240,
    legendInstruction = "Click a series to toggle its line.",
    showLegend = true,
    inspection = "tooltip"
}: MultiAreaChartProps): React.ReactElement {
    const [hidden, setHidden] = useState<Set<string>>(new Set());
    const inspector = useChartInspection();
    const inline = inspection === "inline";
    const visibleSeries = series.filter((s) => !hidden.has(s.key));

    return (
        <>
            <div
                className={`px-2 pt-4 pb-2${inline ? " select-none" : ""}`}
                {...(inline ? inspector.containerProps : {})}
            >
                {inline && <InlineInspectionStyle />}
                {inline && <InlineReadoutSpacer rows={visibleSeries.length} />}
                <ResponsiveContainer width="100%" height={height} minWidth={0}>
                    <AreaChart
                        data={data}
                        margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                        style={inline ? INLINE_CHART_STYLE : undefined}
                    >
                        <defs>
                            {series.map((s) => (
                                <linearGradient
                                    key={s.key}
                                    id={`${gradientIdPrefix}-${s.key}`}
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="0%"
                                        stopColor={s.color}
                                        stopOpacity={0.85}
                                    />
                                    <stop
                                        offset="100%"
                                        stopColor={s.color}
                                        stopOpacity={0.25}
                                    />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid
                            horizontal
                            vertical={false}
                            stroke="rgb(var(--color-foreground-base) / 0.08)"
                        />
                        <XAxis
                            dataKey="t"
                            type="number"
                            scale="time"
                            domain={["dataMin", "dataMax"]}
                            tickFormatter={(v: number) => dateFormatter(v)}
                            axisLine={false}
                            tickLine={false}
                            tick={{
                                fill: "rgb(var(--color-foreground-base) / 0.6)",
                                fontSize: 11,
                                fontFamily: "ABC Social"
                            }}
                            minTickGap={48}
                            padding={{ left: 8, right: 8 }}
                        />
                        <YAxis
                            tickFormatter={(v: number) => yTickFormatter(v)}
                            axisLine={false}
                            tickLine={false}
                            width={64}
                            tick={{
                                fill: "rgb(var(--color-foreground-base) / 0.6)",
                                fontSize: 11,
                                fontFamily: "ABC Social"
                            }}
                        />
                        <Tooltip
                            {...(inline
                                ? inspector.tooltipProps
                                : {
                                      cursor: {
                                          stroke: "rgb(var(--color-foreground-base) / 0.2)"
                                      }
                                  })}
                            content={(props) => {
                                const p = props as unknown as {
                                    active?: boolean;
                                    payload?: ReadonlyArray<{
                                        payload?: MultiAreaRow;
                                    }>;
                                };
                                if (inline) {
                                    const row = p.payload?.[0]?.payload;
                                    return (
                                        <InlineReadout
                                            active={Boolean(p.active)}
                                            x={props.coordinate?.x}
                                            rows={visibleSeries.map((s) => ({
                                                key: s.key,
                                                name: s.name,
                                                color: s.color,
                                                value:
                                                    row?.[s.key] === undefined
                                                        ? null
                                                        : valueFormatter(
                                                              row[s.key]
                                                          )
                                            }))}
                                            date={
                                                row
                                                    ? tooltipDateFormatter(
                                                          row.t
                                                      )
                                                    : null
                                            }
                                            onActiveChange={
                                                inspector.setInspecting
                                            }
                                        />
                                    );
                                }
                                return (
                                    <MultiAreaTooltip
                                        active={p.active}
                                        row={p.payload?.[0]?.payload}
                                        series={series}
                                        hidden={hidden}
                                        valueFormatter={valueFormatter}
                                        tooltipDateFormatter={
                                            tooltipDateFormatter
                                        }
                                    />
                                );
                            }}
                        />
                        {series.map((s) => {
                            if (hidden.has(s.key)) return null;
                            return (
                                <Area
                                    key={s.key}
                                    type="monotone"
                                    dataKey={s.key}
                                    name={s.name}
                                    stroke={s.color}
                                    strokeWidth={2}
                                    fill={`url(#${gradientIdPrefix}-${s.key})`}
                                    dot={false}
                                    activeDot={
                                        inline
                                            ? squareActiveDot(s.color)
                                            : {
                                                  r: 4,
                                                  fill: s.color,
                                                  stroke: "none"
                                              }
                                    }
                                    isAnimationActive
                                    animationDuration={500}
                                />
                            );
                        })}
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            {/* Legend — click a row to toggle visibility. */}
            {showLegend && (
                <Text
                    variant="label"
                    render={<div />}
                    className="px-4 py-3 border-t border-line-base flex flex-wrap items-center gap-x-5 gap-y-2"
                >
                    {series.map((s) => {
                        const isHidden = hidden.has(s.key);
                        return (
                            <button
                                key={s.key}
                                type="button"
                                onClick={() =>
                                    setHidden((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(s.key)) next.delete(s.key);
                                        else next.add(s.key);
                                        return next;
                                    })
                                }
                                className={`inline-flex items-center gap-2 cursor-pointer transition-opacity duration-200 ${
                                    isHidden
                                        ? "opacity-40 hover:opacity-70"
                                        : "hover:opacity-80"
                                }`}
                                aria-pressed={!isHidden}
                            >
                                <span
                                    className="h-[2px] w-5"
                                    style={{ backgroundColor: s.color }}
                                    aria-hidden
                                />
                                <span
                                    className={
                                        isHidden
                                            ? "text-foreground-muted line-through"
                                            : "text-foreground-base"
                                    }
                                >
                                    {s.name}
                                </span>
                            </button>
                        );
                    })}
                    {legendInstruction !== null && (
                        <span className="ml-auto text-foreground-muted">
                            {legendInstruction}
                        </span>
                    )}
                </Text>
            )}
        </>
    );
}

/* ── Tooltip ─────────────────────────────────────────────── */

function MultiAreaTooltip({
    active,
    row,
    series,
    hidden,
    valueFormatter,
    tooltipDateFormatter
}: {
    active?: boolean;
    row?: MultiAreaRow;
    series: MultiAreaSeries[];
    hidden: Set<string>;
    valueFormatter: (value: number) => string;
    tooltipDateFormatter: (value: number) => string;
}): React.ReactElement | null {
    if (!active || !row) return null;
    return (
        <Text
            variant="small"
            render={<div />}
            className="bg-surface-inverse-raised text-foreground-inverse px-3 py-2 min-w-[220px]"
        >
            <Text
                variant="label"
                render={<div />}
                className="text-foreground-inverse-neutral mb-1"
            >
                {tooltipDateFormatter(row.t)}
            </Text>
            <div className="flex flex-col gap-0.5">
                {series.map((s) => {
                    if (hidden.has(s.key)) return null;
                    const v = row[s.key];
                    return (
                        <Text
                            key={s.key}
                            variant="number"
                            render={<div />}
                            className="flex items-baseline justify-between gap-6"
                        >
                            <span className="inline-flex items-center gap-2">
                                <span
                                    className={`h-2 w-2 rounded-[1px] ${GRAPH_BG_CLASS[s.slot]}`}
                                    aria-hidden
                                />
                                <span className="text-foreground-inverse-muted">
                                    {s.name}
                                </span>
                            </span>
                            <span>
                                {v === null || v === undefined
                                    ? "—"
                                    : valueFormatter(v)}
                            </span>
                        </Text>
                    );
                })}
            </div>
        </Text>
    );
}
